import { DOCS_FORM_NAMES, FORM_PDF_PATHS } from "core/consts";
import { useDateLocale } from "core/hooks/useDateLocale";
import { getStaticAsset } from "core/model/config";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { AuctionForm, TransitionalCareFormData } from "core/types";
import { getCachedTemplate } from "dsl/ecosystems/PatientForms/utils/pdfTemplateCache";
import { sanitizePdfFormTextFieldsForStandardFont } from "dsl/ecosystems/PatientForms/utils/sanitizePdfFormTextForStandardFont";
import { PDFDocument, StandardFonts } from "pdf-lib";
import { useCallback } from "react";
import { TransitionalCareFormMapping } from "../RecareDocs/Modal/Forms/TransitionalCare/pdfMapping";

export type CreateTransitionalCarePdfProps = {
  transitionalCare: Readonly<AuctionForm>;
};

export const useCreateTransitionalCarePdf = () => {
  const { currentLocale: locale } = useDateLocale();
  const getOntology = useGetOntology();

  const createTransitionalCarePdf = useCallback(
    async ({ transitionalCare }: CreateTransitionalCarePdfProps) => {
      try {
        const formUrl = getStaticAsset(
          FORM_PDF_PATHS[DOCS_FORM_NAMES.TRANSITIONAL_CARE],
        );
        const formPdfBytes = await getCachedTemplate(formUrl);
        const pdfDoc = await PDFDocument.load(formPdfBytes);
        const form = pdfDoc.getForm();
        const formData = JSON.parse(
          transitionalCare.form_data?.decrypted ?? "{}",
        ) as TransitionalCareFormData;

        TransitionalCareFormMapping({ form, formData, getOntology, locale });
        sanitizePdfFormTextFieldsForStandardFont(form);

        // burn values into PDF so we dont need to render annotation layers
        const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
        form.updateFieldAppearances(font);

        form.flatten();

        const pdfBytes = await pdfDoc.save();

        return pdfBytes;
      } catch (err) {
        const message = "Error saving PDF";
        console.error(message, err);
        throw new Error(message);
      }
    },
    [locale, getOntology],
  );

  return createTransitionalCarePdf;
};
